import axios from 'axios';
import deepmerge from 'deepmerge';

import { asyncApi, handleError } from '@/shared/services/api';
import baseApi from '@/shared/services/bright_finder';

const adminWebhookEndpointEventsApi = asyncApi({
  endpoint: '/admin/webhook_endpoints',
  pathSuffix: '/webhook_events',
});

const adminWebhookEndpointApi = asyncApi({
  endpoint: '/admin/webhook_endpoints',
  payloadKey: 'webhook_endpoint',
});

const webhookEndpointApi = {
  ...adminWebhookEndpointApi,
  eventTypes() {
    return adminWebhookEndpointApi.get('event_types', {}, null, null);
  },
  webhookEvents(webhookEndpointId, params = {}) {
    return adminWebhookEndpointEventsApi.get(webhookEndpointId, params, null, null);
  },
  retryWebhookEvent(webhookEndpointId, webhookEventId) {
    return axios
      .post(
        `${window.api_url}/admin/webhook_endpoints/${webhookEndpointId}/webhook_events/${webhookEventId}/retry`,
      )
      .catch(handleError);
  },
};

const adminApi = {
  admin: {
    apiKey: asyncApi({
      endpoint: '/admin/api_keys',
      payloadKey: 'api_key',
    }),

    apiPermission: asyncApi({
      endpoint: '/admin/api_permissions',
    }),

    dataSubscription: asyncApi({
      endpoint: '/admin/data_subscriptions',
      payloadKey: 'data_subscription',
    }),

    sandbox: asyncApi({
      endpoint: '/admin/sandboxes',
      payloadKey: 'sandbox',
    }),

    sandboxOperation: asyncApi({
      endpoint: '/admin/sandbox_operations',
      payloadKey: 'sandbox_operation',
    }),

    webhookEndpoint: webhookEndpointApi,

    member: {
      baseUrl() {
        return `${window.api_url}/admin/members`;
      },

      create(member, success, failure) {
        axios.post(this.baseUrl(), { member }).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(`${this.baseUrl()}/${id}`).then(success);
      },

      get(id, success, failure) {
        axios.get(`${this.baseUrl()}/${id}`).then(success).catch(failure);
      },

      index(success, failure) {
        axios.get(this.baseUrl()).then(success).catch(failure);
      },

      update(id, member, success, failure) {
        if (!success) return axios.patch(`${this.baseUrl()}/${id}`, { member });

        return axios.patch(`${this.baseUrl()}/${id}`, { member }).then(success).catch(failure);
      },
    },

    organization: {
      baseUrl() {
        return `${window.api_url}/admin/organization`;
      },

      get(success) {
        if (success) {
          return axios.get(this.baseUrl()).then(success);
        }
        return axios.get(this.baseUrl());
      },

      update(organization, success, failure) {
        if (success) {
          return axios.patch(this.baseUrl(), { organization }).then(success).catch(failure);
        }
        return axios.patch(this.baseUrl(), { organization }).catch(handleError);
      },
    },
  },

  organization: {
    brand: asyncApi({
      endpoint: '/organization/brands',
      payloadKey: 'brand',
    }),

    dashboard: asyncApi({
      endpoint: '/organization/dashboards',
      payloadKey: 'dashboard',
    }),

    invoiceBatch: {
      ...asyncApi({
        endpoint: '/organization/invoice_batches',
        payloadKey: 'invoice_batch',
      }),
      async approve(id, successCallback = null, failureCallback = null) {
        return axios
          .patch(`${this.baseUrl(id)}/approve`, this.createPayload({ id }))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) failureCallback(error);
            return handleError(error);
          });
      },
      async removeInvoices(id, invoice_ids, successCallback = null, failureCallback = null) {
        return axios
          .patch(`${this.baseUrl(id)}/remove_invoices`, this.createPayload({ invoice_ids }))
          .then((response) => {
            if (successCallback) successCallback(response);
            return response;
          })
          .catch((error) => {
            if (failureCallback) failureCallback(error);
            return handleError(error);
          });
      },
    },

    metric: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/metrics/${id}`;
        }
        return `${window.api_url}/organization/metrics`;
      },

      create(metric, success, failure) {
        axios.post(this.baseUrl(), metric).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(filters, success) {
        axios.get(this.baseUrl(), { params: filters }).then(success);
      },

      update(id, metric, success, failure) {
        axios.patch(this.baseUrl(id), { metric }).then(success).catch(failure);
      },
    },

    profile: asyncApi({
      endpoint: '/organization/profile',
      payloadKey: 'profile',
      selfWithCredentials: true,
    }),

    question: {
      baseUrl(id) {
        if (id) {
          return `${window.api_url}/organization/questions/${id}`;
        }
        return `${window.api_url}/organization/questions`;
      },

      create(question, success, failure) {
        if (!success) return axios.post(this.baseUrl(), question).catch(handleError);

        axios.post(this.baseUrl(), question).then(success).catch(failure);
      },

      destroy(id, success) {
        axios.delete(this.baseUrl(id)).then(success);
      },

      get(id, success) {
        axios.get(this.baseUrl(id)).then(success);
      },

      index(params, success) {
        axios.get(this.baseUrl(), { params }).then(success);
      },

      update(id, question, success, failure) {
        if (!success) return axios.patch(this.baseUrl(id), { question }).catch(handleError);

        return axios.patch(this.baseUrl(id), { question }).then(success).catch(failure);
      },
    },

    upload: {
      baseUrl() {
        return `${window.api_url}/organization/uploads`;
      },

      create(upload, success, failure) {
        axios.post(this.baseUrl(), { upload }).then(success).catch(failure);
      },

      update(id, upload, success, failure) {
        axios.patch(`${this.baseUrl()}/${id}`, { upload }).then(success).catch(failure);
      },

      syncWatchers(id, watchers_emails, success, failure) {
        axios
          .patch(`${this.baseUrl()}/${id}/sync_watchers`, { watchers_emails })
          .then(success)
          .catch(failure);
      },
    },

    watcher: {
      baseUrl() {
        return `${window.api_url}/organization/watchers`;
      },

      index(params) {
        return axios.get(this.baseUrl(), { params });
      },
    },
  },

  registration: {
    create(registration, success, failure) {
      axios.post(`${window.api_url}/registrations`, { registration }).then(success).catch(failure);
    },
  },
};

export default deepmerge.all([baseApi, adminApi]);
